// src/helpers/howenScoreHelper.js
import dayjs from 'dayjs';
import { apiHowen, apiVss, fleetApi } from '../api/axiosClient';
import FilterBuilder from '../utils/filterBuilder';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

export const HOWEN_ALARM_TYPES = {
  DISTRACTION_1: '118', // Distracted driving
  DISTRACTION_2: '124', // Distracted driving (glance right & left)
  COVERING_CAMERA: '123', // Camera cover
  PHONE_CALL: '116', // Calling
  SLEEPY_EYES_CLOSING: '121', // Eyes closing
  SMOKING: '117', // Smoking
  YAWNING: '122' // Yawning
};

export const HOWEN_EVENT_KEYS = ['yawning', 'smoking', 'sleepy', 'distraction', 'phone_call', 'covering_camera'];

export const EVENT_SOURCE_MAP = {
  over_speed: 'GPS',
  idle_time: 'GPS',
  harsh_braking: 'GPS',
  harsh_acceleration: 'GPS',
  yawning: 'VSS',
  sleepy: 'VSS',
  distraction: 'VSS',
  covering_camera: 'VSS',
  phone_call: 'VSS',
  smoking: 'VSS'
};

export const getEventSource = (eventKey) => {
  return EVENT_SOURCE_MAP[eventKey] || (HOWEN_EVENT_KEYS.includes(eventKey) ? 'VSS' : 'GPS');
};

export const HOWEN_EVENT_ALARM_MAP = {
  phone_call: [HOWEN_ALARM_TYPES.PHONE_CALL],
  smoking: [HOWEN_ALARM_TYPES.SMOKING],
  distraction: [HOWEN_ALARM_TYPES.DISTRACTION_1, HOWEN_ALARM_TYPES.DISTRACTION_2],
  sleepy: [HOWEN_ALARM_TYPES.SLEEPY_EYES_CLOSING],
  yawning: [HOWEN_ALARM_TYPES.YAWNING],
  covering_camera: [HOWEN_ALARM_TYPES.COVERING_CAMERA]
};

/**
 * Check if an event is enabled based on its weight in score_config_events
 * @param {string} eventKey
 * @param {Record<string, number>} scoreConfigEvents
 * @returns {boolean}
 */
export function isEventEnabled(eventKey, scoreConfigEvents) {
  if (!scoreConfigEvents || typeof scoreConfigEvents !== 'object') return true;
  const weight = scoreConfigEvents[eventKey];
  return weight != null && Number(weight) > 0;
}

/**
 * Get active Howen alarm type IDs based on enabled events in score_config_events
 * @param {Record<string, number>} scoreConfigEvents
 * @returns {Array<string>}
 */
export function getActiveHowenAlarmTypes(scoreConfigEvents) {
  const activeTypes = [];
  HOWEN_EVENT_KEYS.forEach((key) => {
    if (isEventEnabled(key, scoreConfigEvents)) {
      const ids = HOWEN_EVENT_ALARM_MAP[key] || [];
      activeTypes.push(...ids);
    }
  });
  return Array.from(new Set(activeTypes));
}

export function calculateEventScore(eventCount, totalRows, weight) {
  const w = Number(weight || 0);
  if (w <= 0) return 0;
  if (!totalRows || totalRows === 0) {
    return 10; // full score jika tidak ada data
  }
  let ratio = (Number(eventCount) || 0) / totalRows;
  if (ratio > 1) ratio = 1;
  const result = (1 - ratio) * 10; // max score selalu 10, tidak dikalikan weight lagi

  return result;
}

export function determineIndexName(pctScore, customIndexes) {
  if (!Array.isArray(customIndexes) || customIndexes.length === 0) {
    console.warn('score_indexes tidak tersedia untuk score_config ini sehingga idx_name tidak bisa ditentukan.');
    return '-';
  }

  const scoreNum = Number(pctScore) || 0;
  const matched = customIndexes.find((idx) => scoreNum >= Number(idx.min_idx ?? 0) && scoreNum <= Number(idx.max_idx ?? 100));

  return matched ? matched.idx_name : '-';
}

/**
 * Sama seperti determineIndexName, tapi kalau skor jatuh di celah antar-range
 * (tidak match range manapun), fallback ke range TERDEKAT dan beri tahu arah
 * pembulatannya.
 * @param {number} pctScore
 * @param {Array} customIndexes
 * @returns {{ idx_name: string, isEstimated: boolean, roundedDirection: 'up'|'down'|null }}
 */
export function determineIndexNameWithFallback(pctScore, customIndexes) {
  if (!Array.isArray(customIndexes) || customIndexes.length === 0) {
    return { idx_name: '-', isEstimated: false, roundedDirection: null };
  }

  const scoreNum = Number(pctScore) || 0;

  const exactMatch = customIndexes.find((idx) => scoreNum >= Number(idx.min_idx ?? 0) && scoreNum <= Number(idx.max_idx ?? 100));
  if (exactMatch) {
    return { idx_name: exactMatch.idx_name, isEstimated: false, roundedDirection: null };
  }

  // Tidak ada match persis -> cari range terdekat
  let nearest = null;
  let nearestDistance = Infinity;
  let direction = null;

  customIndexes.forEach((idx) => {
    const min = Number(idx.min_idx ?? 0);
    const max = Number(idx.max_idx ?? 100);
    let distance;
    let dir;
    if (scoreNum < min) {
      distance = min - scoreNum;
      dir = 'up'; // skor "dibulatkan ke atas" supaya masuk range ini
    } else if (scoreNum > max) {
      distance = scoreNum - max;
      dir = 'down'; // skor "dibulatkan ke bawah" supaya masuk range ini
    } else {
      distance = 0;
      dir = null;
    }
    if (distance < nearestDistance || (distance === nearestDistance && dir === 'up')) {
      nearestDistance = distance;
      nearest = idx;
      direction = dir;
    }
  });

  return {
    idx_name: nearest ? nearest.idx_name : '-',
    isEstimated: !!nearest,
    roundedDirection: direction
  };
}

/**
 * Fetch VTDS records for mapping vehicle_id -> Howen deviceID (integration_imei)
 * @param {string|number} companyId
 * @returns {Promise<Record<string, { deviceId: string, vehicleId: any, licensePlate: string, raw: any }>>}
 */
export async function fetchVtdsHowenMapping(companyId = '') {
  try {
    const fb = new FilterBuilder()
      .setPaging(0, 1000)
      .setOrder(['id'], false)
      .addJoin('company', ['name'])
      .addJoin('vehicle', ['license_plate']);

    if (companyId && companyId !== 'all') {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    }

    const payload = fb.build();
    const response = await fleetApi.post(`/vtds/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : []);

    const map = {};
    (Array.isArray(dataArr) ? dataArr : []).forEach((item) => {
      const vehicleId = item?.vehicle ?? item?.vehicle_id ?? item?.vehicleId ?? item?.id;
      const deviceId = item?.integration_imei;

      if (vehicleId != null && deviceId) {
        map[String(vehicleId)] = {
          deviceId: String(deviceId).trim(),
          vehicleId,
          licensePlate: item?.license_plate ?? item?.join?.vehicle_license_plate ?? item?.vehicle_name ?? '',
          raw: item
        };
      }
    });

    return map;
  } catch (err) {
    console.warn('Failed to fetch VTDS Howen mapping:', err);
    return {};
  }
}

/**
 * Fetch Howen auth token
 * @returns {Promise<string>}
 */
export async function fetchHowenToken() {
  try {
    const resp = await apiHowen.get('/get-token');
    const data = resp?.data ?? resp;
    const token = data?.data?.token ?? data?.token ?? data ?? '';
    return typeof token === 'string' ? token : JSON.stringify(token);
  } catch (err) {
    console.warn('Failed to fetch Howen token:', err);
    return '';
  }
}

/**
 * Fetch all alarm events for a single vehicle within a date range
 * @param {string} deviceId
 * @param {string|dayjs.Dayjs} startDate
 * @param {string|dayjs.Dayjs} endDate
 * @param {string} token
 * @param {Record<string, number>} scoreConfigEvents
 * @returns {Promise<Array>}
 */
export async function fetchVehicleHowenAlarms(deviceId, startDate, endDate, token, scoreConfigEvents = null) {
  if (!deviceId || !startDate || !endDate) return [];

  const activeAlarmTypes = getActiveHowenAlarmTypes(scoreConfigEvents);
  if (activeAlarmTypes.length === 0) {
    // Skip request if all Howen events are disabled/weight 0
    return [];
  }

  const startDayjs = dayjs(startDate);
  const endDayjs = dayjs(endDate);

  const chunks = [];
  let curr = startDayjs.startOf('day');
  const end = endDayjs.startOf('day');

  while (curr.isBefore(end) || curr.isSame(end, 'day')) {
    let chunkStart = curr.startOf('day');
    let chunkEnd = curr.endOf('day');

    if (curr.isSame(startDayjs, 'day')) chunkStart = startDayjs;
    if (curr.isSame(endDayjs, 'day')) chunkEnd = endDayjs;

    chunks.push({
      beginTime: chunkStart.format('YYYY-MM-DD HH:mm:ss'),
      endTime: chunkEnd.format('YYYY-MM-DD HH:mm:ss')
    });

    curr = curr.add(1, 'day');
  }

  const alarmTypeStr = activeAlarmTypes.join(',');

  const promises = chunks.map((chunk) => {
    const payload = {
      alarmType: alarmTypeStr,
      beginTime: chunk.beginTime,
      driverCardId: '',
      endTime: chunk.endTime,
      keyword: '',
      lang: 'en_US',
      pageCount: 2000,
      pageNum: 1,
      scheme: 'http',
      token: token || '',
      deviceID: String(deviceId)
    };

    return apiVss.post('/alarm/apiFindAllByTime.action', payload).catch((err) => {
      console.warn(`Howen alarm fetch error for device ${deviceId}:`, err);
      return null;
    });
  });

  const responses = await Promise.all(promises);

  let combinedList = [];
  responses.forEach((resp) => {
    if (!resp) return;
    const body = resp?.data ?? resp;
    const wrapper = body?.data ?? body;
    const list = Array.isArray(wrapper?.dataList)
      ? wrapper.dataList
      : Array.isArray(wrapper?.rows)
        ? wrapper.rows
        : Array.isArray(wrapper?.result)
          ? wrapper.result
          : [];
    combinedList = [...combinedList, ...list];
  });

  return combinedList;
}

/**
 * Group raw Howen alarms into event_key counts
 * @param {Array} alarms
 * @returns {Record<string, number>}
 */
export function groupHowenAlarmsByEventKey(alarms) {
  const counts = {
    yawning: 0,
    smoking: 0,
    sleepy: 0,
    distraction: 0,
    phone_call: 0,
    covering_camera: 0
  };

  if (!Array.isArray(alarms)) return counts;

  alarms.forEach((item) => {
    const typeId = String(item?.alarmType ?? item?.alarmtype ?? item?.alarmTypeId ?? '').trim();
    const typeName = String(item?.alarmTypeValue ?? item?.alarmTypeName ?? '').toLowerCase();

    if (typeId === HOWEN_ALARM_TYPES.YAWNING || typeName.includes('yawn')) {
      counts.yawning += 1;
    } else if (typeId === HOWEN_ALARM_TYPES.SLEEPY_EYES_CLOSING || typeName.includes('eyes closing') || typeName.includes('eye closing')) {
      counts.sleepy += 1;
    } else if (typeId === HOWEN_ALARM_TYPES.DISTRACTION_1 || typeId === HOWEN_ALARM_TYPES.DISTRACTION_2 || typeName.includes('distract')) {
      counts.distraction += 1;
    } else if (typeId === HOWEN_ALARM_TYPES.COVERING_CAMERA || typeName.includes('cover')) {
      counts.covering_camera += 1;
    } else if (typeId === HOWEN_ALARM_TYPES.PHONE_CALL || typeName.includes('calling') || typeName.includes('phone')) {
      counts.phone_call += 1;
    } else if (typeId === HOWEN_ALARM_TYPES.SMOKING || typeName.includes('smok')) {
      counts.smoking += 1;
    }
  });

  return counts;
}

/**
 * Count violation events grouped by event key
 * @param {Array} events
 * @returns {Record<string, number>}
 */
export function countViolationEventsByKey(events) {
  const counts = { yawning: 0, smoking: 0, sleepy: 0, distraction: 0, phone_call: 0, covering_camera: 0 };
  (events || []).forEach((e) => {
    if (counts[e.event_key] != null) counts[e.event_key] += 1;
  });
  return counts;
}

/**
 * Map raw Howen alarms into a flat list of violation events for display,
 * deduping alarms by guid (or time + plate + violation if guid is missing).
 * @param {Array} alarms
 * @returns {Array<{ event_time: string, start_time: string, vehicle: string, violation: string, event_key: string, raw: any }>}
 */
export function mapHowenAlarmsToViolationEvents(alarms) {
  if (!Array.isArray(alarms)) return [];

  const dedupeMap = new Map();

  alarms.forEach((item) => {
    const time = item?.reportTime || item?.createtime;
    if (!time) return;

    const guid = item?.guid != null ? String(item.guid).trim() : '';
    const plateNo = item?.plateNo || item?.deviceName || '-';
    const alarmTypeValue = item?.alarmTypeValue || '-';

    const dedupeKey = guid || `${time}_${plateNo}_${alarmTypeValue}`;
    if (dedupeMap.has(dedupeKey)) return;

    const typeId = String(item?.alarmType ?? item?.alarmtype ?? '').trim();
    const typeName = String(item?.alarmTypeValue ?? item?.alarmTypeName ?? '').toLowerCase();

    let eventKey = 'distraction';
    if (typeId === HOWEN_ALARM_TYPES.YAWNING || typeName.includes('yawn')) eventKey = 'yawning';
    else if (typeId === HOWEN_ALARM_TYPES.SLEEPY_EYES_CLOSING || typeName.includes('eye')) eventKey = 'sleepy';
    else if (typeId === HOWEN_ALARM_TYPES.COVERING_CAMERA || typeName.includes('cover')) eventKey = 'covering_camera';
    else if (typeId === HOWEN_ALARM_TYPES.PHONE_CALL || typeName.includes('call') || typeName.includes('phone')) eventKey = 'phone_call';
    else if (typeId === HOWEN_ALARM_TYPES.SMOKING || typeName.includes('smok')) eventKey = 'smoking';

    dedupeMap.set(dedupeKey, {
      event_time: time,
      start_time: time,
      vehicle: plateNo,
      violation: alarmTypeValue,
      event_key: eventKey,
      raw: item
    });
  });

  return Array.from(dedupeMap.values());
}

/**
 * Fetch total track records for a device within a date range from Howen VSS
 * @param {string} deviceId
 * @param {string|dayjs.Dayjs} startDate
 * @param {string|dayjs.Dayjs} endDate
 * @param {string} token
 * @returns {Promise<number>}
 */
export async function fetchHowenTotalRows(deviceId, startDate, endDate, token) {
  if (!deviceId || !startDate || !endDate || !token) return 0;
  try {
    const payload = {
      token,
      deviceID: String(deviceId),
      beginTime: dayjs(startDate).format('YYYY-MM-DD HH:mm:ss'),
      endTime: dayjs(endDate).format('YYYY-MM-DD HH:mm:ss'),
      pageNum: '1',
      pageCount: '1'
    };
    const response = await apiVss.post('/track/getApiTrackList.action', payload);
    const count = response?.data?.data?.totalCount ?? response?.data?.totalCount ?? 0;
    return Number(count) || 0;
  } catch (err) {
    console.warn(`Failed to fetch Howen total rows for device ${deviceId}:`, err);
    return 0;
  }
}

/**
 * Enrich and recalculate driver score row with Howen alarm data
 * @param {Object} row
 * @param {Record<string, number>} howenCounts
 * @param {number|null} [vssTotalRows=null]
 * @returns {Object} updated row
 */
export function enrichRowWithHowenEvents(row, howenCounts, vssTotalRows = null) {
  const gpsTotalRows = Number(row?.total_waypoints ?? row?.total_rows ?? 0);
  const effectiveTotalRows = vssTotalRows !== null ? Number(vssTotalRows) : gpsTotalRows;
  const scoreConfigEvents = row?.score_config_events;

  // Filter existing GPS events: keep only enabled events with weight > 0
  let updatedEvents = (Array.isArray(row?.events) ? row.events : []).filter((e) => {
    const key = e.event_key;
    if (!isEventEnabled(key, scoreConfigEvents)) return false;
    if (scoreConfigEvents && scoreConfigEvents[key] != null) {
      return Number(scoreConfigEvents[key]) > 0;
    }
    return Number(e.weight || 0) > 0;
  });

  HOWEN_EVENT_KEYS.forEach((key) => {
    // Check if event is enabled
    if (!isEventEnabled(key, scoreConfigEvents)) {
      updatedEvents = updatedEvents.filter((e) => e.event_key !== key);
      return;
    }

    // Get configured weight for this event key
    let weight = 0;
    if (scoreConfigEvents && scoreConfigEvents[key] != null) {
      weight = Number(scoreConfigEvents[key]);
    } else {
      const existingEv = updatedEvents.find((e) => e.event_key === key);
      if (existingEv && existingEv.weight != null) {
        weight = Number(existingEv.weight);
      }
    }

    if (weight <= 0) {
      updatedEvents = updatedEvents.filter((e) => e.event_key !== key);
      return;
    }

    const count = Number(howenCounts?.[key] || 0);
    const calculatedScore = calculateEventScore(count, effectiveTotalRows, weight);

    const eventObj = {
      event_key: key,
      event_count: count,
      total_rows: effectiveTotalRows,
      weight: weight,
      score: Number(calculatedScore.toFixed(2))
    };

    const existingIndex = updatedEvents.findIndex((e) => e.event_key === key);
    if (existingIndex >= 0) {
      updatedEvents[existingIndex] = {
        ...updatedEvents[existingIndex],
        ...eventObj
      };
    } else {
      updatedEvents.push(eventObj);
    }
  });

  const pctScore = updatedEvents.reduce((sum, e) => sum + ((Number(e.score) || 0) / 10) * Number(e.weight || 0), 0);
  const totalScore = pctScore;

  const scoreIndexes = row?.score_indexes || row?.idx_detail || row?.score_config?.score_indexes;
  const idxName = determineIndexName(pctScore, scoreIndexes);

  return {
    ...row,
    vssTotalRows: vssTotalRows !== null ? Number(vssTotalRows) : null,
    events: updatedEvents,
    total_score: parseFloat(totalScore.toFixed(2)),
    pct_score: parseFloat(pctScore.toFixed(2)),
    idx_name: idxName
  };
}
